import { useQuery } from '@tanstack/react-query';
import { isLinkableTin } from '../lib/tin';
import { useLocale, useT } from '../i18n/locale-context';
import { getSimilarWinners, type SimilarWinner } from '../lib/catalog-client';
import { formatBudgetCompact, pluralRuIndex } from '../lib/format';
import { useHydrated } from '../lib/use-hydrated';
import { LocaleLink } from '../i18n/locale-link';
import { AnalyticsTeaser } from './analytics-teaser';
import { SimilarWinnersTeaserBody } from './analytics-teaser-bodies';
import { countryConfig } from '../country';
import { InfoHint } from './info-hint';
import { splitOrgForm } from './buyer-graph-panel';

/**
 * «Кто выигрывает такие лоты» — the companies that won lots with the same ЕНКТ
 * code over the last 24 months (exact code first, the 6-digit class when the
 * code alone has fewer than three winners). Pro-only: the API answers a guest
 * or a free reader with the LOCKED payload — two counts, never a name — and the
 * card then renders as a blurred teaser on the `similarWinners` wall.
 *
 * Asked for by a Pro client who compared our card with TenderZone's «Возможные
 * участники» (2026-10-09). Same fetch/gating/hide-when-empty contract as
 * `BuyerGraphPanel`: nothing renders when there is no history behind the wall.
 */

const PLURAL_FORMS = ['one', 'few', 'many'] as const;

export function SimilarWinnersPanel({ tenderId }: { tenderId: string }) {
  const t = useT();
  const { locale } = useLocale();
  const hydrated = useHydrated();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['similar-winners', tenderId],
    queryFn: () => getSimilarWinners(tenderId),
    // ⚠️ BROWSER ONLY, like the other analytics cards: the prerender renders
    // ~5k lot pages × 2 locales and must not fan this query out.
    enabled: hydrated,
  });

  const winsLabel = (n: number) =>
    t(`similarWinners.wins.${PLURAL_FORMS[pluralRuIndex(n)]}`, { n });
  const companiesLabel = (n: number) =>
    t(`similarWinners.companies.${PLURAL_FORMS[pluralRuIndex(n)]}`, { n });

  if (isLoading || isError || !data || !data.hasData) return null;

  if (data.locked) {
    return (
      <AnalyticsTeaser
        wall="similarWinners"
        tenderId={tenderId}
        title={t('similarWinners.title')}
        hint={t('similarWinners.hint')}
        note={
          data.awards > 0
            ? t('similarWinners.teaserBasis', {
                awards: winsLabel(data.awards),
                winners: companiesLabel(data.winnerCount),
              })
            : undefined
        }
      >
        {/* Kazakhstan only (`honestTeasers`): no more rows than winners. */}
        <SimilarWinnersTeaserBody
          winners={countryConfig.features.honestTeasers ? data.winnerCount : undefined}
        />
      </AnalyticsTeaser>
    );
  }

  const d = data.result;
  if (!d || d.winners.length === 0) return null;

  // The basis («код/класс ЕНКТ …, N побед, M компаний») lives in the hint, not
  // in a footer: the card is five one-line rows and nothing else (Igor,
  // 2026-10-09 — «компактно, без лишних заголовков»).
  const basis =
    d.tier === 'exact'
      ? t('similarWinners.basisExact', {
          code: d.code,
          awards: winsLabel(d.awards),
          winners: companiesLabel(d.winnerCount),
        })
      : t('similarWinners.basisClass', {
          code: d.classCode ?? d.code,
          awards: winsLabel(d.awards),
          winners: companiesLabel(d.winnerCount),
        });

  return (
    <section
      data-testid="similar-winners"
      className="rounded-lg border border-edge bg-bone-surface px-4 py-3.5 shadow-m-sm sm:px-5"
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <h3 className="text-[14px] font-bold text-ink">{t('similarWinners.title')}</h3>
        <InfoHint text={`${t('similarWinners.hint')} ${basis}.`} />
      </div>
      <ol className="flex flex-col">
        {d.winners.map((w, i) => (
          <WinnerRow key={w.tin} rank={i + 1} winner={w} winsLabel={winsLabel} locale={locale} />
        ))}
      </ol>
    </section>
  );
}

/** One line per winner: rank · name (link) · «N побед · share%». The sum won
 *  rides on the row's tooltip — a second line per row doubled the card. */
function WinnerRow({
  rank,
  winner,
  winsLabel,
  locale,
}: {
  rank: number;
  winner: SimilarWinner;
  winsLabel: (n: number) => string;
  locale: string;
}) {
  const t = useT();
  const { name, form } = splitOrgForm(winner.name ?? winner.tin);
  const nameClass = 'overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold';
  const fullName = winner.name ?? winner.tin;
  const sum =
    winner.totalUzs > 0
      ? `${formatBudgetCompact(String(Math.round(winner.totalUzs)), locale)} ${t('tender.budget.unit')}`
      : null;
  return (
    <li
      title={sum ? `${fullName} — ${sum}` : fullName}
      className="flex items-center gap-2 border-t border-edge-light py-1.5 first:border-t-0"
    >
      <span className="w-3.5 shrink-0 text-[11.5px] font-bold tabular-nums text-ink-muted">
        {rank}
      </span>
      <div className="flex min-w-0 flex-1 items-center">
        {/* Only a 9-digit ИНН has a profile page — see isLinkableTin. */}
        {isLinkableTin(winner.tin) ? (
          <LocaleLink
            to={`/supplier/${winner.tin}`}
            className={`${nameClass} text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand`}
          >
            {name}
          </LocaleLink>
        ) : (
          <span className={`${nameClass} text-ink`}>{name}</span>
        )}
        {form && (
          <span className="ml-1.5 shrink-0 rounded border border-edge-light bg-bone px-1 py-px text-[9.5px] font-bold uppercase tracking-[0.03em] text-ink-muted">
            {form}
          </span>
        )}
      </div>
      <span className="shrink-0 whitespace-nowrap text-[11.5px] tabular-nums text-ink-muted">
        {winsLabel(winner.wins)} ·{' '}
        <span className="font-bold text-ink" title={t('similarWinners.share')}>
          {winner.sharePct.toFixed(1)}%
        </span>
      </span>
    </li>
  );
}
